import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Select, Table, Spin, Calendar, Badge } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, LineChart, Line
} from 'recharts';
import { getData } from '../../../../actions/apiCall';
import moment from 'moment';
import RenderFormItem from '../../../../components/Forms/FormItems';
import { constants } from '../../../../constants/common';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import HRStatsDashboard from './HRStatsDashboard';

const { Title } = Typography;

const HrTargetDashboard = () => {
    const [period, setPeriod] = useState('quarterly');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hrId, setHrId] = useState(null);
    const { user } = useSelector((state: any) => state.app);
    const dispatch = useDispatch();

    useEffect(() => {
        fetchDashboard();
    }, [period, hrId]);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const allHRs = hrId === 'all';
            const resData = await dispatch(getData(`/targets/hr?period=${period}&hrId=${hrId}&allHRs=${allHRs}`));
            if (resData?.code === 200) {
                setStats(resData.data);
            }
        } catch (error) {
            console.error('Error fetching HR Dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const recruitmentColumns = [
        { title: 'Job Title', dataIndex: 'jobTitle', key: 'jobTitle' },
        { title: 'Target', dataIndex: 'target', key: 'target' },
        { title: 'Achieved', dataIndex: 'achieved', key: 'achieved' },
        { title: 'Remaining', dataIndex: 'remaining', key: 'remaining' },
        { title: 'Budget', dataIndex: 'budget', key: 'budget' },
        { title: 'Utilized', dataIndex: 'utilized', key: 'utilized' },
        { title: 'Percentage', dataIndex: 'percentage', key: 'percentage', render: val => `${val}%` },
    ];

    const recruitmentData = stats?.targets?.recruitment?.map((item, index) => ({
        key: index,
        jobTitle: item.jobTitle.name,
        target: item.target,
        achieved: item.achieved,
        remaining: item.remaining,
        percentage: item.percentage,
        utilized: item.utilized,
        budget: item.budget
    })) || [];

    const barChartData = stats?.targets?.recruitment?.map(item => ({
        name: item.jobTitle.name,
        Target: item.target,
        Achieved: item.achieved
    })) || [];

    // Get target display name from value
    const getTargetForName = (value: string) => {
        const target = constants.TARGET_FOR.find(t => t.value === value);
        return target?.name || value;
    };

    return (
        <div className="p-4 min-h-screen">
            <Row justify="space-between" align="middle" className="mb-4">
                <Title level={5}>Recruitment Dashboard</Title>
                <div className='flex items-center gap-2'>
                    {user?.designation === 'Admin' && (
                        <RenderFormItem
                            column={{
                                label: '',
                                name: 'hr',
                                type: 'select',
                                isCombo: true,
                                span: 24,
                                selectProps: {
                                    showSearch: true,
                                    allowClear: true,
                                    placeholder: 'Select HR',
                                    style: { width: 200 },
                                    onChange: (value) => setHrId(value),
                                },
                                extraOptions: [{ name: 'All', value: 'all' }],
                            }}
                            combo={[{
                                name: 'hr',
                                api: '/users',
                                idKey: '_id',
                                render: (text) => <SmallProfileCard name={constants.GET_FULL_NAME(text)} avatar={text?.avatar} />
                            }]}
                        />
                    )}
                    <Select
                        value={period}
                        onChange={setPeriod}
                        options={[
                            { label: 'Weekly', value: 'weekly' },
                            { label: 'Monthly', value: 'monthly' },
                            { label: 'Quarterly', value: 'quarterly' },
                            { label: 'Yearly', value: 'yearly' }
                        ]}
                        style={{ width: 150 }}
                    />
                </div>
            </Row>

            {loading ? (
                <Spin size="large" className="w-full flex justify-center items-center min-h-[200px]" />
            ) : stats ? (
                <>
                    <HRStatsDashboard stats={stats} />
                    <Row gutter={[16, 16]} className="mb-6 mt-4">
                        <Col span={24}>
                            <Card title={`Recruitment Targets (${moment(stats.startDate).format('MMM D')} - ${moment(stats.endDate).format('MMM D')})`}>
                                <Table
                                    columns={recruitmentColumns}
                                    dataSource={recruitmentData}
                                    pagination={false}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24} md={12}>
                            <Card title="Recruitment Target vs Achieved">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={barChartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="Target" fill="#8884d8" />
                                        <Bar dataKey="Achieved" fill="#82ca9d" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card title="Hiring Trend">
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={stats?.lineGraph}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="hires" stroke="#faad14" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col span={12}>
                            {/* Daily Targets Graphs */}
                            {stats?.dailyTargetsGraph && stats.dailyTargetsGraph.length > 0 && (
                                <Row gutter={[16, 16]} className="mb-6">
                                    {stats.dailyTargetsGraph.map((graph: any, index: number) => (
                                        <Col span={24} key={`daily-${index}`}>
                                            <Card title={`Daily Targets - ${getTargetForName(graph.name)}`}>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={graph.data}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis
                                                            dataKey="date"
                                                            tickFormatter={(value) => moment(value).format('MMM DD')}
                                                        />
                                                        <YAxis />
                                                        <Tooltip
                                                            labelFormatter={(value) => moment(value).format('MMM DD, YYYY')}
                                                        />
                                                        <Legend />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="target"
                                                            stroke="#1890ff"
                                                            strokeWidth={2}
                                                            name="Target"
                                                            strokeDasharray="5 5"
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="achieved"
                                                            stroke="#52c41a"
                                                            strokeWidth={2}
                                                            name="Achieved"
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </Col>
                        <Col span={12}>
                            {stats?.weeklyTargetsGraph && stats.weeklyTargetsGraph.length > 0 && (
                                <Row gutter={[16, 16]} className="mb-6">
                                    {stats.weeklyTargetsGraph.map((graph: any, index: number) => (
                                        <Col span={24} key={`weekly-${index}`}>
                                            <Card title={`Weekly Targets - ${getTargetForName(graph.name)}`}>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={graph.data}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis
                                                            dataKey="date"
                                                            tickFormatter={(value) => moment(value).format('MMM DD')}
                                                        />
                                                        <YAxis />
                                                        <Tooltip
                                                            labelFormatter={(value) => moment(value).format('MMM DD, YYYY')}
                                                        />
                                                        <Legend />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="target"
                                                            stroke="#722ed1"
                                                            strokeWidth={2}
                                                            name="Target"
                                                            strokeDasharray="5 5"
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="achieved"
                                                            stroke="#13c2c2"
                                                            strokeWidth={2}
                                                            name="Achieved"
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </Col>
                    </Row>
                    {/* Monthly Targets Graphs */}
                    {stats?.monthlyTargetsGraph && stats.monthlyTargetsGraph.length > 0 && (
                        <Row gutter={[16, 16]} className="mb-6">
                            {stats.monthlyTargetsGraph.map((graph: any, index: number) => (
                                <Col xs={24} md={24} key={`monthly-${index}`}>
                                    <Card title={`Monthly Targets - ${getTargetForName(graph.name)}`}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={graph.data}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="date"
                                                    tickFormatter={(value) => moment(value).format('MMM YYYY')}
                                                />
                                                <YAxis />
                                                <Tooltip
                                                    labelFormatter={(value) => moment(value).format('MMMM YYYY')}
                                                />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="target"
                                                    stroke="#f5222d"
                                                    strokeWidth={2}
                                                    name="Target"
                                                    strokeDasharray="5 5"
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="achieved"
                                                    stroke="#fa8c16"
                                                    strokeWidth={2}
                                                    name="Achieved"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {/* Calendar View */}
                    {stats?.calendarData && (
                        <Row gutter={[16, 16]} className="mb-6">
                            <Col span={24}>
                                <Card title="Daily Target Calendar - Current Month">
                                    <Calendar
                                        fullscreen={false}
                                        cellRender={(date) => {
                                            const dateStr = moment(date.toDate()).format('YYYY-MM-DD');
                                            const dayData = stats.calendarData[dateStr];

                                            if (!dayData) return null;

                                            // Determine dot color based on status
                                            let dotColor = '';
                                            let tooltip = '';

                                            if (dayData.status === 'leave') {
                                                dotColor = '#1890ff'; // Blue
                                                tooltip = 'Leave';
                                            } else if (dayData.status === 'holiday') {
                                                dotColor = '#fa8c16'; // Orange
                                                tooltip = 'Holiday';
                                            } else if (dayData.status === 'weekend') {
                                                dotColor = '#fadb14'; // Yellow
                                                tooltip = 'Weekend';
                                            } else if (dayData.status === 'working' || dayData.status === 'absent') {
                                                if (dayData.targetStatus === 'achieved') {
                                                    dotColor = '#52c41a'; // Green
                                                    tooltip = 'Target Achieved';
                                                } else if (dayData.targetStatus === 'missed') {
                                                    dotColor = '#f5222d'; // Red
                                                    tooltip = 'Target Missed';
                                                } else {
                                                    // No target set or future date
                                                    return null;
                                                }
                                            }

                                            if (!dotColor) return null;

                                            return (
                                                <div style={{ textAlign: 'center', marginTop: '4px' }}>
                                                    <div
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: dotColor,
                                                            display: 'inline-block',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={tooltip}
                                                    />
                                                </div>
                                            );
                                        }}
                                    />
                                    <div style={{ marginTop: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#52c41a' }}></div>
                                            <span>Target Achieved</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#f5222d' }}></div>
                                            <span>Target Missed</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#1890ff' }}></div>
                                            <span>Leave</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#fa8c16' }}></div>
                                            <span>Holiday</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#fadb14' }}></div>
                                            <span>Weekend</span>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            ) : (
                <p>No data available</p>
            )
            }
        </div >
    );
};

export default HrTargetDashboard;
